<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useBotsStore } from '@/features/bots/store'
import RobotOutlineIcon from 'vue-material-design-icons/RobotOutline.vue'
import CoreSliderField from '@/components/core/form/fields/CoreSliderField.vue'
import type { Bot } from '@/types/domain'

const emit = defineEmits<{
  (event: 'dirty-change', value: boolean): void
}>()

const props = withDefaults(defineProps<{
  bot?: Bot | null
  save?: (values: {context_size: number; max_tokens: number; temperature: number}) => Promise<boolean>
  disabled?: boolean
}>(), {
  bot: null,
})
const router = useRouter()
const botsStore = useBotsStore()

const activeBot = computed<Bot | null>(() => props.bot ?? null)
const isSaving = ref<boolean>(false)

const form = ref({
  context_size: 10000,
  max_tokens: 1000,
  temperature: 0.7,
})

const initialForm = ref({
  context_size: 10000,
  max_tokens: 1000,
  temperature: 0.7,
})

const formatTemperature = (value: number): string => value.toFixed(1)
const formatMaxTokens = (value: number): string => value === 0 ? 'Unlimited' : String(value)

watch(
  activeBot,
  (bot: Bot | null) => {
    if (!bot || isSaving.value) return
    const nextForm = {
      context_size: bot.context_size ?? 10000,
      max_tokens: bot.max_tokens ?? 1000,
      temperature: bot.temperature ?? 0.7,
    }
    form.value = { ...nextForm }
    initialForm.value = { ...nextForm }
  },
  { immediate: true },
)

const isDirty = computed(() => JSON.stringify(form.value) !== JSON.stringify(initialForm.value))

watch(
  isDirty,
  (value) => {
    emit('dirty-change', value)
  },
  { immediate: true },
)

const saveBotSettings = async (): Promise<void> => {
  if (!activeBot.value) return
  isSaving.value = true
  try {
    if (props.save) {
      if (!await props.save({...form.value})) return
    } else await botsStore.updateBot(activeBot.value.id, {
      context_size: form.value.context_size,
      max_tokens: form.value.max_tokens,
      temperature: form.value.temperature,
    })
    initialForm.value = { ...form.value }
  } catch (error: unknown) {
    console.error('Failed to update bot settings:', error)
  } finally {
    isSaving.value = false
  }
}

const openSettings = (): void => {
  router.push('/settings')
}
</script>

<template>
  <div class="action-menu-bot">
    <div v-if="!activeBot" class="action-menu-bot__empty">
      <RobotOutlineIcon class="action-menu-bot__icon" aria-hidden="true" />
      <div class="action-menu-bot__title">No bot selected</div>
      <button type="button" class="m-btn m-btn--ghost" @click="openSettings">
        Open settings
      </button>
    </div>

    <div v-else class="action-menu-bot__content">
      <div class="action-menu-bot__info">
        <span class="m-eyebrow">Model</span>
        <div class="action-menu-bot__model">{{ activeBot.model || 'Unnamed bot' }}</div>
      </div>

      <div class="space-y-3">
        <CoreSliderField
          v-model="form.context_size"
          name="context_size"
          label="Context Size"
          :min="1024"
          :max="128000"
          :step="1024"
          :show-value="true"
        />
        <CoreSliderField
          v-model="form.max_tokens"
          name="max_tokens"
          label="Max Tokens"
          :min="0"
          :max="1000"
          :step="1"
          :show-value="true"
          :format-value="formatMaxTokens"
        />
        <CoreSliderField
          v-model="form.temperature"
          name="temperature"
          label="Temperature"
          :min="0"
          :max="2"
          :step="0.1"
          :format-value="formatTemperature"
        />
      </div>

      <div class="action-menu-bot__actions">
        <button type="button" class="m-btn m-btn--ghost" @click="openSettings">
          Full settings
        </button>
        <button
          type="button"
          class="m-btn m-btn--primary"
          :disabled="disabled || isSaving || !isDirty"
          @click="saveBotSettings"
        >
          {{ isSaving ? 'Saving...' : 'Save' }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.action-menu-bot {
  padding: 16px 18px 18px;
  color: var(--manuscript-ink);
}

.action-menu-bot__content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.action-menu-bot__info {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.action-menu-bot__model {
  font: 400 1.15rem/1.25 var(--manuscript-serif);
  color: var(--manuscript-ink);
}

.action-menu-bot__actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
}

.action-menu-bot__empty {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-start;
}

.action-menu-bot__icon {
  width: 22px;
  height: 22px;
  color: var(--manuscript-muted);
}

.action-menu-bot__icon svg {
  width: 100%;
  height: 100%;
}

.action-menu-bot__title {
  font: 400 1rem/1.3 var(--manuscript-serif);
  color: var(--manuscript-ink);
}
</style>
